/*! Header inline search: default panel on focus, predictive on input */
function isDesktopSearch() {
  return window.matchMedia("(min-width: 47.9375em)").matches;
}

function markHeaderSearchReady() {
  if (!isDesktopSearch()) return;

  document
    .querySelectorAll(".header.header--desk-inline-search")
    .forEach((header) => header.classList.add("header-inline-search-ready"));
}

function markMobileHeaderReady() {
  if (isDesktopSearch()) return;

  document
    .querySelectorAll(".header.header--desk-inline-search")
    .forEach((header) => header.classList.add("header-inline-search-ready"));
}

function syncRecentSearchesLayout(host, defaultPanel) {
  const recentSearches = host.querySelector("recent-searches");
  if (!defaultPanel) return;

  if (!recentSearches) {
    defaultPanel.classList.add("header-search-panel__grid--no-recent");
    defaultPanel.classList.remove("has-recent-searches");
    return;
  }

  if (typeof recentSearches.update === "function") {
    recentSearches.update();
  }

  const list = recentSearches.querySelector("ul");
  const hasRecent = Boolean(list && list.childElementCount > 0);

  if (!hasRecent) {
    recentSearches.setAttribute("hidden", "");
    recentSearches.classList.add("is-empty");
    recentSearches.classList.remove("has-items");
    recentSearches.style.setProperty("display", "none", "important");
    defaultPanel.classList.remove("has-recent-searches");
  } else {
    recentSearches.removeAttribute("hidden");
    recentSearches.classList.remove("is-empty");
    recentSearches.classList.add("has-items");
    recentSearches.style.removeProperty("display");
    defaultPanel.classList.add("has-recent-searches");
  }

  defaultPanel.classList.toggle("header-search-panel__grid--no-recent", !hasRecent);
}

function positionMobileDropdown(host) {
  if (isDesktopSearch() || !host.classList.contains("header-inline-search--mobile")) return;

  const field = host.querySelector(".header-inline-search__field");
  const dropdown = host.querySelector(".header-inline-search__dropdown");
  if (!field || !dropdown) return;

  const bottomGap = 80;
  const rect = field.getBoundingClientRect();
  const availableHeight = window.innerHeight - rect.bottom - bottomGap;

  dropdown.style.top = `${rect.bottom}px`;
  dropdown.style.left = `${rect.left}px`;
  dropdown.style.width = `${rect.width}px`;
  dropdown.style.right = "auto";
  dropdown.style.bottom = "auto";
  dropdown.style.maxHeight = `${Math.max(availableHeight, 160)}px`;
}

function setMobileSearchOpen(isOpen) {
  document.body.classList.toggle("header-search-mobile-open", isOpen && !isDesktopSearch());
}

function isMobileDropdownVisible(host) {
  return (
    host.classList.contains("header-inline-search--show-default") || host.hasAttribute("open")
  );
}

function initHeaderInlineSearchHost(host) {
  if (host.dataset.headerSearchInit) return;
  host.dataset.headerSearchInit = "1";

  const input = host.querySelector('input[type="search"]');
  const defaultPanel = host.querySelector(".header-search-panel__default");
  const results = host.querySelector("predictive-search-results");
  const closeBtn = host.querySelector(".js-header-search-close");
  if (!input) return;

  const recentSearches = host.querySelector("recent-searches");
  const isMobile = host.classList.contains("header-inline-search--mobile");

  const showDefault = () => {
    if (!input.value.trim() && !host.hasAttribute("open")) {
      if (recentSearches) recentSearches.update();
      syncRecentSearchesLayout(host, defaultPanel);
      host.classList.add("header-inline-search--show-default");
      if (defaultPanel) defaultPanel.removeAttribute("hidden");
      if (results) results.setAttribute("hidden", "");
      if (isMobile) {
        positionMobileDropdown(host);
        setMobileSearchOpen(true);
      }
    }
  };

  const hideDefault = () => {
    host.classList.remove("header-inline-search--show-default");
    if (defaultPanel && host.hasAttribute("open")) {
      defaultPanel.setAttribute("hidden", "");
    }
  };

  const closeMobileSearch = () => {
    host.removeAttribute("open");
    host.classList.remove("header-inline-search--show-default");
    if (defaultPanel) defaultPanel.setAttribute("hidden", "");
    if (results) results.setAttribute("hidden", "");
    input.setAttribute("aria-expanded", "false");
    input.blur();
    setMobileSearchOpen(false);
  };

  const syncMobileState = () => {
    if (!isMobile) return;

    if (isMobileDropdownVisible(host) || document.activeElement === input) {
      positionMobileDropdown(host);
      setMobileSearchOpen(true);
    } else {
      setMobileSearchOpen(false);
    }
  };

  input.addEventListener("focus", () => {
    showDefault();
    syncMobileState();
  });

  input.addEventListener("input", () => {
    if (input.value.trim()) {
      hideDefault();
      if (defaultPanel) defaultPanel.setAttribute("hidden", "");
    } else {
      showDefault();
    }
    syncMobileState();
  });

  host.addEventListener("focusout", () => {
    setTimeout(() => {
      if (!host.contains(document.activeElement)) {
        host.classList.remove("header-inline-search--show-default");
        if (!host.hasAttribute("open")) {
          setMobileSearchOpen(false);
        }
      }
    });
  });

  closeBtn?.addEventListener("click", closeMobileSearch);

  if (isMobile) {
    const reposition = () => positionMobileDropdown(host);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, { passive: true });
  }

  new MutationObserver(() => {
    if (host.hasAttribute("open")) {
      hideDefault();
      if (defaultPanel) defaultPanel.setAttribute("hidden", "");
    }
    syncMobileState();
    if (isMobile && host.hasAttribute("open")) {
      requestAnimationFrame(() => positionMobileDropdown(host));
    }
  }).observe(host, { attributes: true, attributeFilter: ["open"] });
}

function initHeaderInlineSearch() {
  if (isDesktopSearch()) {
    markHeaderSearchReady();
    if (typeof theme !== "undefined" && typeof theme.setHeaderStyle === "function") {
      theme.setHeaderStyle();
    }
  } else if (typeof theme !== "undefined" && typeof theme.revealMobileHeaderWhenReady === "function") {
    theme.revealMobileHeaderWhenReady();
  } else {
    markMobileHeaderReady();
    if (typeof theme !== "undefined" && typeof theme.setHeaderStyle === "function") {
      theme.setHeaderStyle();
    }
  }

  document.querySelectorAll("predictive-search.header-inline-search").forEach(initHeaderInlineSearchHost);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderInlineSearch);
} else {
  initHeaderInlineSearch();
}

document.addEventListener("shopify:section:load", initHeaderInlineSearch);

window.markMobileHeaderReady = markMobileHeaderReady;
